import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  useToast,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { FaSave, FaTimes, FaEllipsisV } from "react-icons/fa";
import { cqAPI } from "../../lib/Auth";
import { getCampaignPreviewHtml, PHONE_CAMPAIGN_FIELDS } from "../Campaigns/CampaignConstants";
import SendTestModal from "./SendTestModal";

function CampaignPreviewModal({ isOpen, onClose, campaigns, onCampaignUpdate, onSendTest }) {
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [editedBody, setEditedBody] = useState("");
  const [editedPhoneFields, setEditedPhoneFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSendTestOpen, setIsSendTestOpen] = useState(false);
  const [selectedCampaignForTest, setSelectedCampaignForTest] = useState(null);
  const [previewHtmlCache, setPreviewHtmlCache] = useState({});
  const [loadingIds, setLoadingIds] = useState({});
  const toast = useToast();

  const campaignsByType = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return {};
    const grouped = {};
    campaigns.forEach((campaign) => {
      const type = campaign.type || "unknown";
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(campaign);
    });
    return grouped;
  }, [campaigns]);

  const types = useMemo(() => Object.keys(campaignsByType), [campaignsByType]);

  const loadCampaignPreview = async (campaign) => {
    if (previewHtmlCache[campaign._id]) return;
    setLoadingIds((prev) => ({ ...prev, [campaign._id]: true }));
    try {
      console.log("Loading preview for campaign:", campaign._id, "type:", campaign.type, "template:", campaign.template);
      const html = await getCampaignPreviewHtml(campaign);
      console.log("Preview HTML result:", campaign._id, html?.substring(0, 100));
      setPreviewHtmlCache((prev) => ({ ...prev, [campaign._id]: html }));
    } catch (err) {
      console.log("Preview loading failed with error", err);
      setPreviewHtmlCache((prev) => ({ ...prev, [campaign._id]: "" }));
    } finally {
      setLoadingIds((prev) => ({ ...prev, [campaign._id]: false }));
    }
  };

  useEffect(() => {
    if (isOpen && campaigns) {
      campaigns.forEach((campaign) => {
        if (!previewHtmlCache[campaign._id]) {
          loadCampaignPreview(campaign);
        }
      });
    }
  }, [isOpen, campaigns]);

  useEffect(() => {
    if (!isOpen) {
      setEditingCampaignId(null);
      setEditedBody("");
      setEditedPhoneFields({});
      setSelectedCampaignForTest(null);
      setPreviewHtmlCache({});
      setLoadingIds({});
    }
  }, [isOpen]);

  const handleEditClick = (campaign) => {
    setEditingCampaignId(campaign._id);
    if (campaign.type === "phone") {
      const phone = campaign.template?.phone ?? {};
      const fields = {};
      PHONE_CAMPAIGN_FIELDS.forEach((f) => {
        fields[f.key] = phone[f.key]?.text ?? "";
      });
      setEditedPhoneFields(fields);
    } else if (campaign.type === "sms") {
      setEditedBody(campaign.template?.sms?.template ?? "");
    } else if (campaign.type === "email") {
      setEditedBody(campaign.template?.email?.email_content ?? previewHtmlCache[campaign._id] ?? "");
    } else {
      setEditedBody("");
    }
  };

  const handleCancelEdit = () => {
    setEditingCampaignId(null);
    setEditedBody("");
    setEditedPhoneFields({});
  };

  const handleSaveEdit = async (campaignId, campaign) => {
    setIsSaving(true);
    try {
      const payload = { campaignId };
      if (campaign?.type === "phone") {
        payload.phoneTemplate = editedPhoneFields;
      } else if (campaign?.type === "sms") {
        payload.smsTemplate = editedBody;
      } else if (campaign?.type === "email") {
        payload.emailContent = editedBody;
      }

      const response = await cqAPI.put("/campaigns/body", payload);

      if (response.type === "ok") {
        toast({
          title: "Campaign updated",
          description: "Campaign has been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        if (onCampaignUpdate) {
          onCampaignUpdate(
            campaignId,
            campaign?.type === "phone" ? editedPhoneFields : editedBody,
            campaign?.type
          );
        }
        setEditingCampaignId(null);
        setEditedBody("");
        setEditedPhoneFields({});
        // Invalidate preview cache so it reloads
        setPreviewHtmlCache((prev) => {
          const updated = { ...prev };
          delete updated[campaignId];
          return updated;
        });
      } else {
        throw new Error(response.msg || "Failed to update campaign");
      }
    } catch (error) {
      toast({
        title: "Error updating campaign",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTestClick = (campaign) => {
    setSelectedCampaignForTest(campaign);
    setIsSendTestOpen(true);
  };

  const handleSendTestClose = () => {
    setIsSendTestOpen(false);
    setSelectedCampaignForTest(null);
  };

  const handleSendTestSubmit = (testData) => {
    if (onSendTest) {
      onSendTest({
        ...testData,
        campaigns: selectedCampaignForTest ? [selectedCampaignForTest] : testData.campaigns,
      });
    }
    setIsSendTestOpen(false);
    setSelectedCampaignForTest(null);
  };

  const renderCampaignContent = (campaign) => {
    const isEditing = editingCampaignId === campaign._id;

    return (
      <Box
        key={campaign._id}
        p={4}
        bg="white"
        borderRadius="md"
        border="1px"
        borderColor="gray.200"
        mb={4}
      >
        <HStack justify="space-between" mb={3}>
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold">{campaign.name || "Untitled Campaign"}</Text>
            <Badge colorScheme="teal" textTransform="capitalize">
              {campaign.type}
            </Badge>
          </VStack>
          {!isEditing && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Icon as={FaEllipsisV} />}
                variant="ghost"
                size="sm"
                aria-label="Options"
              />
              <MenuList>
                <MenuItem onClick={() => handleEditClick(campaign)}>
                  Edit
                </MenuItem>
                <MenuItem onClick={() => handleSendTestClick(campaign)}>
                  Send Test
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </HStack>

        {isEditing ? (
          <VStack align="stretch" spacing={3}>
            {campaign.type === "phone" ? (
              PHONE_CAMPAIGN_FIELDS
                .filter((f) =>
                  campaign.purpose === "campaign-manual"
                    ? f.manual
                    : f.key !== "call_context"
                )
                .filter((f) => !f.aiOnly || campaign?.settings?.ai_enabled)
                .filter((f) => !f.readOnly)
                .map((field) => (
                  <Box key={field.key}>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      {field.label}
                    </Text>
                    <Textarea
                      value={editedPhoneFields[field.key] ?? ""}
                      onChange={(e) =>
                        setEditedPhoneFields((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      minH="80px"
                      resize="vertical"
                    />
                  </Box>
                ))
            ) : (
              <Textarea
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                minH="150px"
                resize="vertical"
                placeholder="Enter campaign body..."
              />
            )}
            <HStack justify="flex-end" spacing={2}>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FaTimes />}
                onClick={handleCancelEdit}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<FaSave />}
                onClick={() => handleSaveEdit(campaign._id, campaign)}
                isLoading={isSaving}
              >
                Save
              </Button>
            </HStack>
          </VStack>
        ) : loadingIds[campaign._id] ? (
          <Box p={3} bg="gray.50" borderRadius="md" display="flex" justifyContent="center">
            <Spinner size="sm" />
          </Box>
        ) : previewHtmlCache[campaign._id] ? (
          <Box
            p={3}
            bg="gray.50"
            borderRadius="md"
            fontSize="sm"
            dangerouslySetInnerHTML={{ __html: previewHtmlCache[campaign._id] }}
          />
        ) : (
          <Box p={3} bg="gray.50" borderRadius="md" fontSize="sm">
            <Text>No content available</Text>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>Campaign Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {types.length > 0 ? (
              <Tabs variant="enclosed" colorScheme="teal">
                <TabList>
                  {types.map((type) => (
                    <Tab key={type} textTransform="capitalize">
                      {type}
                      {campaignsByType[type].length > 1 && (
                        <Badge ml={2} colorScheme="gray">
                          {campaignsByType[type].length}
                        </Badge>
                      )}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {types.map((type) => (
                    <TabPanel key={type}>
                      <VStack align="stretch" spacing={4}>
                        {campaignsByType[type].map((campaign) =>
                          renderCampaignContent(campaign)
                        )}
                      </VStack>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            ) : (
              <Text color="gray.500">No campaigns available</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SendTestModal
        isOpen={isSendTestOpen}
        onClose={handleSendTestClose}
        onSubmit={handleSendTestSubmit}
        campaigns={selectedCampaignForTest ? [selectedCampaignForTest] : campaigns}
      />
    </>
  );
}

export default CampaignPreviewModal;